import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Shield, Loader2, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useProviderAuthSettings,
  useUpdateProviderAuthSettings,
  useCustomerAuthSettings,
  useUpdateCustomerAuthSettings,
} from '@/hooks/provider/useProviderAuthSettings';
import { ProviderAuthSettings } from '@/types/provider-auth-settings';
import { ProviderAuthSettingsRequestError } from '@/services/provider/provider-auth-settings.service';

type FormValues = {
  passwordLoginEnabled: boolean;
  minLength: number;
  requireNumbers: boolean;
  requireSymbols: boolean;
  requireMixedCase: boolean;
  disallowPrevious: number;
};

const toFormValues = (settings: ProviderAuthSettings): FormValues => ({
  passwordLoginEnabled: settings.passwordLoginEnabled,
  minLength: settings.passwordPolicy.minLength,
  requireNumbers: settings.passwordPolicy.requireNumbers,
  requireSymbols: settings.passwordPolicy.requireSymbols,
  requireMixedCase: settings.passwordPolicy.requireMixedCase,
  disallowPrevious: settings.passwordPolicy.disallowPrevious,
});

const DEFAULT_FORM_VALUES: FormValues = {
  passwordLoginEnabled: false,
  minLength: 12,
  requireNumbers: true,
  requireSymbols: true,
  requireMixedCase: true,
  disallowPrevious: 5,
};

interface ProviderPasswordLoginCardProps {
  scope?: 'provider' | 'customer';
}

export function ProviderPasswordLoginCard({ scope = 'provider' }: ProviderPasswordLoginCardProps) {
  const { t } = useTranslation();
  const query = scope === 'provider' ? useProviderAuthSettings() : useCustomerAuthSettings();
  const updateMutation =
    scope === 'provider' ? useUpdateProviderAuthSettings() : useUpdateCustomerAuthSettings();
  const { data, isLoading, isError, refetch, error } = query;
  const [formValues, setFormValues] = useState<FormValues>(DEFAULT_FORM_VALUES);

  useEffect(() => {
    if (data) {
      setFormValues(toFormValues(data));
    }
  }, [data]);

  const handleInputChange = (field: keyof FormValues, value: number | boolean) => {
    setFormValues((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const isDirty = useMemo(() => {
    if (!data) {
      return false;
    }
    const current = toFormValues(data);
    return (
      current.passwordLoginEnabled !== formValues.passwordLoginEnabled ||
      current.minLength !== formValues.minLength ||
      current.requireNumbers !== formValues.requireNumbers ||
      current.requireSymbols !== formValues.requireSymbols ||
      current.requireMixedCase !== formValues.requireMixedCase ||
      current.disallowPrevious !== formValues.disallowPrevious
    );
  }, [data, formValues]);

  const isSaving = updateMutation.isPending;
  const passwordDisabled = !formValues.passwordLoginEnabled;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formValues) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        passwordLoginEnabled: formValues.passwordLoginEnabled,
        passwordPolicy: formValues.passwordLoginEnabled
          ? {
              minLength: formValues.minLength,
              requireNumbers: formValues.requireNumbers,
              requireSymbols: formValues.requireSymbols,
              requireMixedCase: formValues.requireMixedCase,
              disallowPrevious: formValues.disallowPrevious,
            }
          : null,
      });
      toast.success(
        t(
          'settings.passwordAuth.saveSuccess',
          scope === 'provider'
            ? 'Provider password settings saved.'
            : 'Customer password settings saved.',
        ),
      );
    } catch (error) {
      console.error('Failed to update password login settings:', error);
      toast.error(
        t(
          'settings.passwordAuth.saveError',
          'Unable to update password login configuration. Please try again.',
        ),
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {scope === 'provider'
            ? t('settings.passwordAuth.title', 'Password login')
            : t('settings.passwordAuth.customerTitle', 'Customer password login')}
        </CardTitle>
        <CardDescription>
          {scope === 'provider'
            ? t(
                'settings.passwordAuth.providerDescription',
                'Allow provider users to sign in with email and password in addition to magic links and social logins.',
              )
            : t(
                'settings.passwordAuth.customerDescription',
                'Allow customers to sign in to their portal with email and password in addition to magic links.',
              )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>
                {error instanceof ProviderAuthSettingsRequestError && error.status === 401
                  ? t(
                      'settings.passwordAuth.unauthorized',
                      'Unable to load password settings with this session. Provide a Provider PAT or use a privileged token.',
                    )
                  : t(
                      'settings.passwordAuth.loadError',
                      'Unable to load password login settings. Refresh or try again later.',
                    )}
              </span>
              <Button variant="secondary" size="sm" onClick={() => refetch()} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : data ? (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium">
                  {t('settings.passwordAuth.enableLabel', 'Enable password login')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {scope === 'provider'
                    ? t(
                        'settings.passwordAuth.enableDescription',
                        'Adds an email and password form to the provider login page.',
                      )
                    : t(
                        'settings.passwordAuth.customerEnableDescription',
                        'Adds an email and password form to the customer login page.',
                      )}
                </p>
              </div>
              <Switch
                checked={formValues.passwordLoginEnabled}
                onCheckedChange={(checked) => handleInputChange('passwordLoginEnabled', checked)}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <p className="font-medium">{t('settings.passwordAuth.policyTitle', 'Password policy')}</p>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'settings.passwordAuth.policyDescription',
                    'Define the minimum requirements passwords must meet. These rules only apply when password login is enabled.',
                  )}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="minLength">
                    {t('settings.passwordAuth.minLengthLabel', 'Minimum length')}
                  </Label>
                  <Input
                    id="minLength"
                    type="number"
                    min={8}
                    value={formValues.minLength}
                    disabled={passwordDisabled || isSaving}
                    onChange={(event) =>
                      handleInputChange('minLength', Number(event.target.value) || 0)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="disallowPrevious">
                    {t('settings.passwordAuth.historyLabel', 'Disallow previous passwords')}
                  </Label>
                  <Input
                    id="disallowPrevious"
                    type="number"
                    min={0}
                    value={formValues.disallowPrevious}
                    disabled={passwordDisabled || isSaving}
                    onChange={(event) =>
                      handleInputChange('disallowPrevious', Number(event.target.value) || 0)
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      'settings.passwordAuth.historyDescription',
                      'How many previous passwords each user cannot reuse.',
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="font-medium">
                      {t('settings.passwordAuth.requireNumbers', 'Require numbers')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.passwordAuth.requireNumbersDescription', 'Password must include at least one number.')}
                    </p>
                  </div>
                  <Switch
                    checked={formValues.requireNumbers}
                    disabled={passwordDisabled || isSaving}
                    onCheckedChange={(checked) => handleInputChange('requireNumbers', checked)}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="font-medium">
                      {t('settings.passwordAuth.requireSymbols', 'Require symbols')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.passwordAuth.requireSymbolsDescription', 'Password must include at least one punctuation or symbol character.')}
                    </p>
                  </div>
                  <Switch
                    checked={formValues.requireSymbols}
                    disabled={passwordDisabled || isSaving}
                    onCheckedChange={(checked) => handleInputChange('requireSymbols', checked)}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="font-medium">
                      {t('settings.passwordAuth.requireMixedCase', 'Require mixed case')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.passwordAuth.requireMixedCaseDescription', 'Password must include both uppercase and lowercase letters.')}
                    </p>
                  </div>
                  <Switch
                    checked={formValues.requireMixedCase}
                    disabled={passwordDisabled || isSaving}
                    onCheckedChange={(checked) => handleInputChange('requireMixedCase', checked)}
                  />
                </div>
              </div>
            </div>

            {passwordDisabled && (
              <Alert>
                <AlertDescription>
                  {t(
                    'settings.passwordAuth.policyDisabledHint',
                    'Password rules apply only when password login is enabled.',
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={!isDirty || isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('settings.passwordAuth.saving', 'Saving…')}
                  </>
                ) : (
                  t('settings.passwordAuth.saveCta', 'Save configuration')
                )}
              </Button>
            </div>
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
}
